import styled, { keyframes } from 'styled-components';
import {
  Button,
  Card,
  FormLabel,
  FormInput,
  FormSelect,
  FormCheckbox,
  Container,
  Tag,
  ModalOverlay as BaseModalOverlay,
  ModalContent as BaseModalContent
} from './index';

// Animation keyframes

const floatAnimation = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0); }
`;

// Main container
export const InternshipContainer = styled(Container)`
  max-width: 1200px;
  padding: 2rem;
`;

// Glassy filters container
export const FiltersContainer = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.25);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.2);
`;

export const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const FilterGroup = styled.div`
  flex: 1;
  min-width: 200px;
`;

export const FilterLabel = styled(FormLabel)`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
`;

export const FilterSelect = styled(FormSelect)`
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(99, 102, 241, 0.3);
  color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  appearance: none;
  padding-right: 2.5rem;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='rgba(255, 255, 255, 0.7)' viewBox='0 0 16 16'><path d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/></svg>");
  background-repeat: no-repeat;
  background-position: calc(100% - 1rem) center;
  
  &:hover {
    background-color: rgba(15, 23, 42, 0.6);
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 10px rgba(99, 102, 241, 0.2);
  }
  
  &:focus {
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.3);
    outline: none;
  }
  
  option {
    background: rgba(15, 23, 42, 0.95);
    color: rgba(255, 255, 255, 0.9);
  }
`;

export const FilterInput = styled(FormInput)`
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(99, 102, 241, 0.3);
  color: rgba(255, 255, 255, 0.9);
  
  &:focus {
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.3);
    outline: none;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

export const FilterCheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.75rem;
`;

export const FilterCheckbox = styled(FormCheckbox)`
  width: 18px;
  height: 18px;
  accent-color: rgba(99, 102, 241, 0.8);
`;

export const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

export const ClearButton = styled(Button)`
  background: rgba(239, 68, 68, 0.15);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-radius: 10px;
  padding: 0.7rem 1.4rem;
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 0.95rem;
  letter-spacing: 0.2px;
  
  &:hover {
    background: rgba(239, 68, 68, 0.25);
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(239, 68, 68, 0.1);
  }
`;

// Internship list and cards
export const InternshipList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.75rem;
  margin-top: 1.5rem;
`;

export const InternshipCard = styled(Card)`
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.2);
  padding: 1.5rem;
  margin-bottom: 1.25rem;
  overflow: hidden;
  position: relative;
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
    
    &:before {
      opacity: 1;
    }
  }
  
  /* Animated glow effect on hover */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      120deg,
      transparent, 
      transparent,
      rgba(99, 102, 241, 0.08),
      transparent,
      transparent
    );
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
    border-radius: 14px;
  }
`;

export const CompanyInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  width: 65%;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const CompanyDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  
  h3 {
    margin: 0;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    font-size: 1.2rem;
    letter-spacing: 0.2px;
    line-height: 1.3;
  }
  
  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.95rem;
    letter-spacing: 0.1px;
  }
`;

export const InternshipDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
  
  p {
    margin: 0 0 0.75rem 0;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.95rem;
    line-height: 1.5;
  }
  
  strong {
    color: rgba(255, 255, 255, 0.95);
    font-weight: 500;
    letter-spacing: 0.2px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

export const CompanyLogo = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(99, 102, 241, 0.4);
  background: rgba(15, 23, 42, 0.6);
  position: relative;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s ease;
  }
  
  /* Shiny effect on top */
  &:after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent,
      rgba(255, 255, 255, 0.15),
      transparent
    );
    transform: rotate(45deg);
    transition: opacity 0.4s ease;
    opacity: 0.5;
  }
  
  ${InternshipCard}:hover & {
    border-color: rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 18px rgba(99, 102, 241, 0.5);
    
    img {
      transform: scale(1.05);
    }
    
    &:after {
      opacity: 1;
    }
  }
`;

export const LocationPin = styled.span`
  color: rgba(99, 102, 241, 0.9);
  font-size: 1rem;
  display: flex;
  align-items: center;
`;

export const LocationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.925rem;
  margin-top: 0.25rem;
  
  span {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
`;

export const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
  align-items: center;
  justify-content: flex-start;
`;

export const InternshipTag = styled(Tag)`
  background: rgba(99, 102, 241, 0.15);
  color: rgba(255, 255, 255, 0.9);
  padding: 0.35rem 0.85rem;
  font-size: 0.875rem;
  border: 1px solid rgba(99, 102, 241, 0.3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 70px;
  border-radius: 20px;
  font-weight: 500;
  letter-spacing: 0.2px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  }

  &.paid {
    background: rgba(16, 185, 129, 0.15);
    color: rgba(16, 185, 129, 0.9);
    border: 1px solid rgba(16, 185, 129, 0.3);
    
    &:hover {
      background: rgba(16, 185, 129, 0.25);
      box-shadow: 0 3px 8px rgba(16, 185, 129, 0.15);
    }
  }
  
  &.unpaid {
    background: rgba(245, 158, 11, 0.15);
    color: rgba(245, 158, 11, 0.9);
    border: 1px solid rgba(245, 158, 11, 0.3);
    
    &:hover {
      background: rgba(245, 158, 11, 0.25);
      box-shadow: 0 3px 8px rgba(245, 158, 11, 0.15);
    }
  }
`;

export const DeadlineInfo = styled.div`
  text-align: right;
  color: ${props => props.expired ? 'rgba(239, 68, 68, 0.9)' : 'rgba(255, 255, 255, 0.9)'};
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  background: ${props => 
    props.expired ? 'rgba(239, 68, 68, 0.1)' : 
    props.lastDay ? 'rgba(245, 158, 11, 0.1)' : 
    'rgba(99, 102, 241, 0.1)'};
  border: 1px solid ${props => 
    props.expired ? 'rgba(239, 68, 68, 0.2)' : 
    props.lastDay ? 'rgba(245, 158, 11, 0.2)' : 
    'rgba(99, 102, 241, 0.2)'};

  p {
    margin: 0 0 0.25rem 0;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .days-remaining {
    margin: 0;
    font-size: 0.75rem;
    font-weight: bold;
    color: ${props => 
      props.expired ? 'rgba(239, 68, 68, 0.9)' : 
      props.lastDay ? 'rgba(245, 158, 11, 0.9)' : 
      'rgba(255, 255, 255, 0.7)'};
  }
`;

export const ApplyButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

export const DeadlineApplyContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  width: 35%;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-top: 1rem;
  }
`;

export const ApplyButton = styled(Button)`
  background: ${props => props.disabled ? 'rgba(15, 23, 42, 0.5)' : 'rgba(79, 70, 229, 0.75)'};
  color: white;
  border: 1px solid ${props => props.disabled ? 'rgba(15, 23, 42, 0.3)' : 'rgba(99, 102, 241, 0.3)'};
  border-radius: 10px;
  padding: 0.7rem 1.4rem;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.3px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin-top: 1rem;
  position: relative;
  overflow: hidden;
  z-index: 1; /* Ensure clickability */
  box-shadow: ${props => props.disabled ? 'none' : '0 4px 10px rgba(79, 70, 229, 0.2)'};
  
  &:hover:not(:disabled) {
    background: rgba(79, 70, 229, 0.95);
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(79, 70, 229, 0.25);
    
    &:before {
      left: 100%;
    }
  }
  
  &:active:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(79, 70, 229, 0.2);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.7s ease;
    display: ${props => props.disabled ? 'none' : 'block'};
  }
`;

export const ContentSection = styled.div`
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  }

  h4 {
    color: rgba(255, 255, 255, 0.95);
    margin: 0 0 0.75rem 0;
    font-weight: 600;
    font-size: 1.05rem;
    position: relative;
    display: inline-block;
    letter-spacing: 0.2px;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 50%;
      height: 2px;
      background: linear-gradient(90deg, rgba(99, 102, 241, 0.8), transparent);
      border-radius: 2px;
    }
  }

  p {
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.5;
    margin: 0;
    font-size: 0.95rem;
    max-height: 4.5em;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
`;

export const RequirementsList = styled.ul`
  margin: 0;
  padding-left: 1.5rem;
  
  li {
    color: rgba(255, 255, 255, 0.85);
    margin-bottom: 0.75rem;
    padding-left: 0.5rem;
    position: relative;
    font-size: 0.95rem;
    line-height: 1.5;
    
    &:before {
      content: '';
      position: absolute;
      left: -1rem;
      top: 0.5rem;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: rgba(99, 102, 241, 0.7);
      box-shadow: 0 0 4px rgba(99, 102, 241, 0.4);
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const PostedDate = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 1rem;
  
  p {
    margin: 0;
  }
`;

// Modal components
export const ModalOverlay = styled(BaseModalOverlay)`
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  z-index: 1100; /* Ensure it's above everything else */
`;

export const ModalContent = styled(BaseModalContent)`
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  animation: ${floatAnimation} 4s infinite ease-in-out;
  
  &::-webkit-scrollbar {
    width: 8px;
    background: rgba(15, 23, 42, 0.3);
    border-radius: 8px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.5);
    border-radius: 8px;
    
    &:hover {
      background: rgba(99, 102, 241, 0.7);
    }
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    padding-bottom: 1rem;
  }
  
  .deadline-apply-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
    min-height: 80px;
  }
  
  .apply-button-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.5rem;
  }
`;

export const CompanyText = styled.div`
  h3 {
    margin: 0 0 0.5rem 0;
    color: rgba(255, 255, 255, 0.98);
    font-size: 1.3rem;
    font-weight: 600;
    letter-spacing: 0.3px;
    line-height: 1.3;
    
    ${InternshipCard}:hover & {
      background: linear-gradient(90deg, rgba(255, 255, 255, 0.98), rgba(99, 102, 241, 0.9));
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      text-fill-color: transparent;
    }
  }
  
  p {
    margin: 0 0 0.25rem 0;
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    letter-spacing: 0.1px;
  }
`;

// Create an alias for CardHeader to maintain compatibility with existing code
export const InternshipCardHeader = CardHeader;
